import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TextField, Typography } from '@mui/material';
import {
  useListingField,
  useFieldUpdater,
  ListingResource,
} from '@brightmls/nextgen.lae.flc';
import { PropertyField } from '../../../../../system/field';

interface LevelCountInputProps {
  fieldKey: PropertyField;
  label: string;
  isError?: boolean;
}

const externalToDisplay = (v: unknown) =>
  v === null || v === undefined ? '' : String(v);

const COMMIT_DEBOUNCE_MS = 400;

/**
 * Minimal per-level count input. Local state drives the <input>'s value so
 * typing doesn't depend on the store round-trip. Commits to the listing store
 * on a short debounce (so the summary / header totals still update as the user
 * types) and immediately on blur / unmount.
 *
 * Writing to the store on every keystroke was cascading back up through
 * useListingResource → roomsByLevel → sortedLevelsAndRooms → dnd-kit's
 * SortableContext/useSortable re-registration, which was enough to knock
 * focus off the active <input>. Debouncing the commit breaks the cascade.
 */
export const LevelCountInput: React.FC<LevelCountInputProps> = React.memo(
  ({ fieldKey, label, isError = false }) => {
    const field = useListingField(ListingResource.Property, fieldKey, '');
    const updateFields = useFieldUpdater();

    const [localValue, setLocalValue] = useState<string>(() =>
      externalToDisplay(field.value),
    );
    // What we last pushed to the store. Prevents the echo from our own write
    // from resetting local state via the sync effect below.
    const lastWrittenRef = useRef<string>(externalToDisplay(field.value));
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingRef = useRef<string | null>(null);

    const commit = useCallback(
      (raw: string) => {
        pendingRef.current = null;
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = null;
        }
        lastWrittenRef.current = raw;
        if (raw === '') {
          updateFields({ [fieldKey]: null });
          return;
        }
        const n = Number(raw);
        if (Number.isFinite(n) && n >= 0) {
          updateFields({ [fieldKey]: n });
        }
      },
      [fieldKey, updateFields],
    );

    // Sync external changes (hydration from the listing, another path writing
    // the field). Skipped when the change is just our own echo.
    useEffect(() => {
      const external = externalToDisplay(field.value);
      if (external === lastWrittenRef.current) return;
      if (pendingRef.current !== null) return; // user is mid-edit
      lastWrittenRef.current = external;
      setLocalValue(external);
    }, [field.value]);

    // Flush any pending commit on unmount so values aren't lost.
    useEffect(() => {
      return () => {
        if (pendingRef.current !== null) {
          commit(pendingRef.current);
        }
      };
    }, [commit]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setLocalValue(raw);
        pendingRef.current = raw;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          debounceRef.current = null;
          const pending = pendingRef.current;
          if (pending !== null) commit(pending);
        }, COMMIT_DEBOUNCE_MS);
      },
      [commit],
    );

    const handleBlur = useCallback(() => {
      if (pendingRef.current !== null) commit(pendingRef.current);
    }, [commit]);

    return (
      <>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            color: isError ? 'error.main' : 'text.primary',
            fontSize: {
              xs: '16px',
              sm: '10px',
              md: '16px',
            },
            fontStyle: 'normal',
            lineHeight: '24px',
          }}
        >
          {label}
        </Typography>
        <TextField
          id={fieldKey}
          fullWidth
          variant="outlined"
          size="small"
          type="number"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown')
              e.preventDefault();
          }}
          error={isError}
          inputProps={{ min: 0 }}
          sx={{
            maxWidth: '240px',
            '& .MuiInputBase-input': {
              padding: '8px 12px',
              textAlign: 'left',
              fontWeight: 400,
            },
          }}
        />
      </>
    );
  },
);

LevelCountInput.displayName = 'LevelCountInput';

export default LevelCountInput;
